import React, {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  createRef,
  RefObject,
  useContext,
} from "react";

import { FaTimesCircle } from "react-icons/fa";

import "./typeahead.styles.scss";
import GetFieldSuggest from "../../api/GetFieldSuggest";
import ProductContext from "../../context/ProductContext";

interface TypeAheadProps {
  placeholder: string;
  initialValue?: string;
  onInputChange: (value?: string) => void;
  productNumberContext: (value: string) => void;
  isInvalid: boolean;
}

const TypeAhead = ({
  placeholder,
  productNumberContext,
  onInputChange,
  initialValue,
  isInvalid,
}: TypeAheadProps) => {
  const productContext: any = useContext(ProductContext);

  const {
    productDispatch,
  }: {
    productDispatch: any;
  } = productContext;

  const useKeyPress = function (
    targetKey: string,
    ref: RefObject<HTMLElement>,
  ) {
    const [keyPressed, setKeyPressed] = useState(false);

    function downHandler({ key }: { key: string }) {
      if (key === targetKey) {
        setKeyPressed(true);
      }
    }

    const upHandler = ({ key }: { key: string }) => {
      if (key === targetKey) {
        setKeyPressed(false);
      }
    };

    React.useEffect(() => {
      ref.current?.addEventListener("keydown", downHandler);
      ref.current?.addEventListener("keyup", upHandler);

      return () => {
        ref.current?.removeEventListener("keydown", downHandler);
        ref.current?.removeEventListener("keyup", upHandler);
      };
    });

    return keyPressed;
  };

  const [items, setItems] = useState<itemType[]>([]);
  //const i = items[0];
  type itemType = {
    id: number;
    index: number;
    name: string;
    highlightedValue: string;
  };
  type ListItemType = {
    item: itemType;
    active: boolean;
    setSelected: Dispatch<SetStateAction<itemType | undefined>>;
  };
  const ListItem = ({ item, active, setSelected }: ListItemType) => (
    <li
      className={`item ${active ? "active" : ""}`}
      onMouseDown={(e) => setSelected(item)}
      dangerouslySetInnerHTML={{ __html: item.highlightedValue }}
    />
  );

  const searchBoxInput = createRef<HTMLInputElement>();
  const searchBox = createRef<HTMLDivElement>();
  const [selected, setSelected] =
    useState<React.SetStateAction<itemType | undefined>>(undefined);
  // const downPress = useKeyPress("ArrowDown", searchBox);
  // const upPress = useKeyPress("ArrowUp", searchBox);
  const enterPress = useKeyPress("Enter", searchBox);
  const downPressInput = useKeyPress("ArrowDown", searchBoxInput);
  const upPressInput = useKeyPress("ArrowUp", searchBoxInput);
  const enterPressInput = useKeyPress("Enter", searchBoxInput);
  const [cursor, setCursor] = useState<number>(0);
  const [forceChange, setForceChange] = useState<boolean>(true);
  const [searchItem, setSearchItem] = useState<string>(initialValue ?? "");
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [initial, setInitial] = useState<boolean>(true);

  const passProductToParent = (selected: any) => productNumberContext(selected);
  const [suggestionClicked, setClicked] = useState(false);

  const handleClickOutside = (evt: Event) => {
    if (
      evt.target instanceof HTMLElement &&
      searchBox.current?.contains(evt.target)
    ) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    if (searchItem) {
      onInputChange && onInputChange(searchItem);
      GetFieldSuggest(searchItem).then((data) => {
        // const values = data.values.map((d: any[]) => {
        //   return d.value;
        // });
        //console.log(data);
        setItems(data);
      });
    }
  }, [searchItem]);

  useEffect(() => {
    // add when mounted
    document.addEventListener("mousedown", handleClickOutside);
    // return function to be called when unmounted
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchBox]);

  const handelInputChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setSelected(undefined);
    setSearchItem(e.currentTarget.value);
    setShowSuggestions(true);
  };
  // const handelInputKeyPress = (e: React.SyntheticEvent<HTMLInputElement>) => {
  //   setSelected(undefined);
  //   setSearchItem(e.currentTarget.value);
  //   setShowSuggestions(true);
  // };
  const handelSuggestionClick = (
    selected: SetStateAction<itemType | undefined>,
  ) => {
    if (selected) {
      setSelected(selected);
      setSearchItem(selected.name);
      passProductToParent(selected.name);
      setShowSuggestions(false);
      setClicked((prevState) => (prevState = true));
    }
  };
  const handleOnClick = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setShowSuggestions(true);
  };
  const handleOnFocus = () => {
    setClicked((prevState) => (prevState = false));
    setShowSuggestions(true);
  };
  const handleOnBlur = () => {
    setShowSuggestions(false);
  };

  useEffect(() => {
    if (initial && initialValue) {
      setSearchItem(initialValue);
      setInitial(false);
    }
  }, [initialValue]);

  useEffect(() => {
    if (downPressInput) {
      if (items.length === 1) {
        setCursor(0);
        setForceChange(!forceChange);
      }
      setCursor((prevState) =>
        prevState < items.length - 1 ? prevState + 1 : prevState,
      );

      setShowSuggestions(true);
    }
  }, [downPressInput]);

  useEffect(() => {
    if (upPressInput) {
      if (items.length === 1) {
        setCursor(0);
        setForceChange(!forceChange);
      }
      setCursor((prevState) => (prevState > 0 ? prevState - 1 : prevState));
      setShowSuggestions(true);
    }
  }, [upPressInput]);

  useEffect(() => {
    if (cursor > -1 && items.length >= cursor) {
      setSelected(items[cursor]);
    }
  }, [cursor]);

  useEffect(() => {
    selected && onInputChange && onInputChange(selected.name);
    //selected && setSearchItem(selected.name);
  }, [selected]);

  useEffect(() => {
    setCursor(-1);
  }, [items]);

  useEffect(() => {
    if (enterPressInput || enterPress) {
      productDispatch({
        type: "SET_SUBMITTED",
        payload: true,
      });
      if (searchBoxInput.current) {
        setCursorPosition(searchBoxInput);
      }
      setShowSuggestions(false);
    }
  }, [enterPress, enterPressInput]);

  useEffect(() => {
    if (selected && suggestionClicked) {
      if (searchBoxInput.current) {
        setCursorPosition(searchBoxInput);
        setShowSuggestions(false);
      }
    }
  });

  function setCursorPosition(searchBoxInput: any) {
    if (searchBoxInput.current) {
      searchBoxInput.current?.focus();
      searchBoxInput.current.selectionEnd = searchBoxInput.current.value.length;
    }
  }

  return (
    <div className="inputWrap">
      <input
        ref={searchBoxInput}
        className={`input ${isInvalid ? "input_error" : ""}`}
        type="text"
        placeholder={placeholder}
        onChange={handelInputChange}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onClick={handleOnClick}
        value={selected ? selected.name : searchItem}
      />
      {isInvalid === true && (
        <div className="inputWrap-errorIcon">
          <FaTimesCircle fontSize="20px" />
        </div>
      )}
      <ul
        className="pricing-availability-type-ahead"
        style={{ visibility: showSuggestions ? "visible" : "hidden" }}
      >
        {items &&
          items.map((item, i) => (
            <ListItem
              key={item.index}
              active={i === cursor}
              item={item}
              setSelected={handelSuggestionClick}
            />
          ))}
      </ul>
    </div>
  );
};

export default TypeAhead;

//https://stackoverflow.com/questions/42036865/react-how-to-navigate-through-list-by-arrow-keys
